import { getToken, removeToken } from './auth';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

let workspaceIdForApi = null;

export const setWorkspaceIdForApi = (id) => {
  workspaceIdForApi = id;
};

export const clearWorkspaceIdForApi = () => {
  workspaceIdForApi = null;
};

export const publicApiRequest = async (endpoint, options = {}) => {
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  let response;
  try {
    response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers,
    });
  } catch (error) {
    console.error('Network error:', error);
    throw new Error('Network error: ' + error.message);
  }

  const text = await response.text();
  let data;
  try {
    data = text ? JSON.parse(text) : {};
  } catch (parseError) {
    if (!response.ok) {
      throw new Error(text || 'Request failed');
    }
    throw new Error('Invalid response from server');
  }

  if (!response.ok) {
    throw new Error(data.error || data.message || text || 'Request failed');
  }

  return data;
};

export const apiRequest = async (endpoint, options = {}) => {
  const token = getToken();
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  if (workspaceIdForApi) {
    headers['X-Workspace-ID'] = workspaceIdForApi;
  }

  let response;
  try {
    response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers,
    });
  } catch (error) {
    console.error('Network error:', error);
    throw new Error('Network error: ' + error.message);
  }

  if (response.status === 401) {
    removeToken();
    window.location.href = '/login';
    throw new Error('Unauthorized');
  }

  const text = await response.text();
  let data;
  try {
    data = text ? JSON.parse(text) : {};
  } catch (parseError) {
    if (!response.ok) {
      throw new Error(text || 'Request failed');
    }
    throw new Error('Invalid response from server');
  }

  if (!response.ok) {
    throw new Error(data.error || data.message || text || 'Request failed');
  }

  return data;
};

export const login = async (email, password) => {
  console.log('login function called with:', { email, password });
  try {
    const result = await apiRequest('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    console.log('login function result:', result);
    return result;
  } catch (error) {
    console.error('login function error:', error);
    throw error;
  }
};

export const getUsers = async () => {
  return apiRequest('/users');
};

export const getUser = async (id) => {
  return apiRequest(`/users/${id}`);
};

export const createUser = async (userData) => {
  return apiRequest('/users', {
    method: 'POST',
    body: JSON.stringify(userData),
  });
};

export const updateUser = async (id, userData) => {
  return apiRequest(`/users/${id}`, {
    method: 'PUT',
    body: JSON.stringify(userData),
  });
};

export const deleteUser = async (id) => {
  return apiRequest(`/users/${id}`, {
    method: 'DELETE',
  });
};

export const approveUser = async (id) => {
  return apiRequest(`/users/${id}/approve`, {
    method: 'POST',
  });
};

export const blockUser = async (id) => {
  return apiRequest(`/users/${id}/block`, {
    method: 'POST',
  });
};

export const getProducts = async () => {
  return apiRequest('/products');
};

export const getProduct = async (id) => {
  return apiRequest(`/products/${id}`);
};

export const createProduct = async (productData) => {
  return apiRequest('/products', {
    method: 'POST',
    body: JSON.stringify(productData),
  });
};

export const updateProduct = async (id, productData) => {
  return apiRequest(`/products/${id}`, {
    method: 'PUT',
    body: JSON.stringify(productData),
  });
};

export const deleteProduct = async (id) => {
  return apiRequest(`/products/${id}`, {
    method: 'DELETE',
  });
};

export const getSKUs = async (productId) => {
  return apiRequest(`/products/${productId}/skus`);
};

export const getSKU = async (skuId) => {
  return apiRequest(`/skus/${skuId}`);
};

export const createSKU = async (productId, skuData) => {
  return apiRequest(`/products/${productId}/skus`, {
    method: 'POST',
    body: JSON.stringify(skuData),
  });
};

export const updateSKU = async (skuId, skuData) => {
  return apiRequest(`/skus/${skuId}`, {
    method: 'PUT',
    body: JSON.stringify(skuData),
  });
};

export const deleteSKU = async (skuId) => {
  return apiRequest(`/skus/${skuId}`, {
    method: 'DELETE',
  });
};

// Product Attributes
export const getProductAttributes = async (productId) => {
  return apiRequest(`/products/${productId}/attributes`);
};

export const createProductAttribute = async (productId, attributeData) => {
  return apiRequest(`/products/${productId}/attributes`, {
    method: 'POST',
    body: JSON.stringify(attributeData),
  });
};

export const updateProductAttribute = async (productId, attributeId, attributeData) => {
  return apiRequest(`/products/${productId}/attributes/${attributeId}`, {
    method: 'PUT',
    body: JSON.stringify(attributeData),
  });
};

export const getAffectedSKUsByAttribute = async (productId, attributeId) => {
  return apiRequest(`/products/${productId}/attributes/${attributeId}/affected-skus`);
};

export const deleteProductAttribute = async (productId, attributeId, confirm = false) => {
  const url = confirm ? `/products/${productId}/attributes/${attributeId}?confirm=true` : `/products/${productId}/attributes/${attributeId}`;
  return apiRequest(url, {
    method: 'DELETE',
  });
};

// Product Attribute Options
export const createProductAttributeOption = async (productId, attributeId, optionData) => {
  return apiRequest(`/products/${productId}/attributes/${attributeId}/options`, {
    method: 'POST',
    body: JSON.stringify(optionData),
  });
};

export const getAffectedSKUsByOption = async (productId, attributeId, optionId) => {
  return apiRequest(`/products/${productId}/attributes/${attributeId}/options/${optionId}/affected-skus`);
};

export const deleteProductAttributeOption = async (productId, attributeId, optionId, confirm = false) => {
  const url = confirm ? `/products/${productId}/attributes/${attributeId}/options/${optionId}?confirm=true` : `/products/${productId}/attributes/${attributeId}/options/${optionId}`;
  return apiRequest(url, {
    method: 'DELETE',
  });
};

export const getWorkspaces = async () => {
  return apiRequest('/workspaces');
};

export const createWorkspace = async (data) => {
  return apiRequest('/workspaces', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export const deleteWorkspace = async (id) => {
  return apiRequest(`/workspaces/${id}`, {
    method: 'DELETE',
  });
};

export const getMyProfile = async () => {
  return apiRequest('/auth/me');
};

export const updateMyProfile = async (data) => {
  return apiRequest('/auth/me', {
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

export const uploadPhoto = async (file) => {
  const token = getToken();
  const formData = new FormData();
  formData.append('photo', file);

  const response = await fetch(`${API_URL}/auth/me/photo`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: formData,
  });

  if (response.status === 401) {
    removeToken();
    window.location.href = '/login';
    throw new Error('Unauthorized');
  }

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || data.message || 'Upload failed');
  }

  return data;
};

export const getMedias = async () => {
  return apiRequest('/medias');
};

export const getMedia = async (id) => {
  return apiRequest(`/medias/${id}`);
};

export const uploadMedia = async (formData) => {
  const token = getToken();
  const headers = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (workspaceIdForApi) headers['X-Workspace-ID'] = workspaceIdForApi;

  const response = await fetch(`${API_URL}/medias`, {
    method: 'POST',
    headers,
    body: formData,
  });

  if (response.status === 401) {
    removeToken();
    window.location.href = '/login';
    throw new Error('Unauthorized');
  }

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || data.message || 'Upload failed');
  }

  return data;
};

export const deleteMedia = async (id) => {
  return apiRequest(`/medias/${id}`, {
    method: 'DELETE',
  });
};

export const getMediaLinks = async (id) => {
  return apiRequest(`/medias/${id}/links`);
};

export const linkMedia = async (mediaId, metadata) => {
  return apiRequest(`/medias/${mediaId}/link`, {
    method: 'POST',
    body: JSON.stringify({ media_id: mediaId, metadata }),
  });
};

export const unlinkMedia = async (mediaId, metadataKey, metadataValue) => {
  return apiRequest(`/medias/${mediaId}/unlink`, {
    method: 'POST',
    body: JSON.stringify({ metadata_key: metadataKey, metadata_value: metadataValue }),
  });
};

export const updateMediaOrder = async (metadataKey, entityId, mediaOrders) => {
  return apiRequest('/medias/order', {
    method: 'POST',
    body: JSON.stringify({
      metadata_key: metadataKey,
      entity_id: entityId,
      media_orders: mediaOrders,
    }),
  });
};

export const updateMediaMetadata = async (mediaId, metadataKey, entityId, title, url) => {
  return apiRequest(`/medias/${mediaId}/metadata`, {
    method: 'PUT',
    body: JSON.stringify({
      metadata_key: metadataKey,
      entity_id: entityId,
      title: title,
      url: url,
    }),
  });
};

export const getCustomers = async () => {
  return apiRequest('/customers');
};

export const getCustomer = async (id) => {
  return apiRequest(`/customers/${id}`);
};

export const createCustomer = async (data) => {
  return apiRequest('/customers', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export const updateCustomer = async (id, data) => {
  return apiRequest(`/customers/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

export const deleteCustomer = async (id) => {
  return apiRequest(`/customers/${id}`, {
    method: 'DELETE',
  });
};

export const getOrders = async () => {
  return apiRequest('/orders');
};

export const getOrder = async (id) => {
  return apiRequest(`/orders/${id}`);
};

export const createOrder = async (data) => {
  return apiRequest('/orders', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export const updateOrder = async (id, data) => {
  return apiRequest(`/orders/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

export const approveOrder = async (id) => {
  return apiRequest(`/orders/${id}/approve`, {
    method: 'POST',
  });
};

export const addOrderLine = async (orderId, data) => {
  return apiRequest(`/orders/${orderId}/lines`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export const addOrderLineItem = async (orderId, lineId, data) => {
  return apiRequest(`/orders/${orderId}/lines/${lineId}/items`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export const updateOrderLine = async (orderId, lineId, data) => {
  return apiRequest(`/orders/${orderId}/lines/${lineId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

export const deleteOrderLine = async (orderId, lineId) => {
  return apiRequest(`/orders/${orderId}/lines/${lineId}`, {
    method: 'DELETE',
  });
};

export const updateOrderLineItem = async (orderId, lineId, itemId, data) => {
  return apiRequest(`/orders/${orderId}/lines/${lineId}/items/${itemId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

export const deleteOrderLineItem = async (orderId, lineId, itemId) => {
  return apiRequest(`/orders/${orderId}/lines/${lineId}/items/${itemId}`, {
    method: 'DELETE',
  });
};

export const getOrderCustomizationFields = async (orderId) => {
  return apiRequest(`/orders/${orderId}/customization-fields`);
};

export const upsertOrderCustomizationFields = async (orderId, fields) => {
  return apiRequest(`/orders/${orderId}/customization-fields`, {
    method: 'PUT',
    body: JSON.stringify({ fields }),
  });
};

export const upsertOrderLineItemCustomizationFields = async (orderId, lineId, itemId, fields) => {
  return apiRequest(`/orders/${orderId}/lines/${lineId}/items/${itemId}/customization-fields`, {
    method: 'PUT',
    body: JSON.stringify({ fields }),
  });
};

export const deleteOrderLineItemCustomizationField = async (orderId, lineId, itemId, fieldId) => {
  return apiRequest(`/orders/${orderId}/lines/${lineId}/items/${itemId}/customization-fields/${fieldId}`, {
    method: 'DELETE',
  });
};

export const getOrderPublicForm = async (workspaceId, token) => {
  return publicApiRequest(`/public/orders/form/${workspaceId}/${token}`);
};

export const submitOrderPublicForm = async (workspaceId, token, data) => {
  return publicApiRequest(`/public/orders/form/${workspaceId}/${token}`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export const getSettings = async () => {
  return apiRequest('/settings');
};

export const updateSettings = async (data) => {
  return apiRequest('/settings', {
    method: 'PUT',
    body: JSON.stringify(data),
  });
};